import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  TableContainer as MuiTableContainer,
  Table as MuiTable,
  TableBody as MuiTableBody,
  InputAdornment as MuiInputAdornment,
  TableHead as MuiTableHead,
  Tooltip as MuiTooltip,
  Paper as MuiPaper,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  TextField as MuiTextField,
} from "@mui/material";
import { useFormikContext } from "formik";
import { studyTargetAccrualsStyles } from "./StudyTargetAccrualsStyles";
import { roundTo } from "../../../shared/utils";
import { useStudies } from "../../../services/studiesService";
import { NumericFormat } from "react-number-format";
import { Theme } from "common";

const applyFormat = (val, format) => {
  if (val === undefined || val === null) return "";

  if (typeof val === "object" && "displayName" in val) return val.displayName;

  if (typeof format === "function") return format(val);

  return val.toString();
};

const headCells = [
  {
    id: "group",
    label: ["head_label_group"],
    width: "16em",
    //    tooltip: "head_tooltip_group",
    totalRender: "",
  },
  {
    id: "value",
    label: ["head_label_value"],
    //    tooltip: "head_tooltip_value",
    align: "right",
    totalRender: "TOTAL",
    width: "16em",
  },
  {
    id: "benchmark",
    label: ["head_label_benchmark"],
    format: (x) => (x * 100).toFixed(2) + "%",
    width: "14em",
    align: "right",
    totalRender: (val) => applyFormat(val, (x) => (x * 100).toFixed(2) + "%"),
  },
  {
    id: "target",
    label: ["head_label_target"],
    format: (x) => (x * 100).toFixed(2) + "%",
    width: "14em",
    align: "right",
    totalRender: (val) => applyFormat(val, (x) => (x * 100).toFixed(2)),
  },
  {
    id: "actual",
    label: ["head_label_actual"],
    format: (x) => (!isNaN(x) ? (x * 100).toFixed(2) + "%" : "-"),
    width: "16em",
    align: "right",
    totalRender: (val, targetAccruals) =>
      targetAccruals
        ? applyFormat(val, (x) => (!isNaN(x) ? (x * 100).toFixed(2) + "%" : ""))
        : "",
  },
  {
    id: "delta",
    label: ["head_label_delta"],
    format: (x) => (!isNaN(x) ? (x * 100).toFixed(2) + "%" : "-"),
    width: "10em",
    align: "right",
    totalRender: "",
  },
  {
    id: "ranking",
    label: ["head_label_ranking"],
    width: "2em",
    align: "center",
    totalRender: "",
  },
];

const StudyTargetAccrualsTable = ({ isOptimizationEnabled }) => {
  const { values, setFieldTouched, setFieldValue } = useFormikContext();
  const { t } = useTranslation();

  const {
    targetAccruals,
    protocolTargetAccrual,
    protocolTargetAccrualc,
    protocolSummaryAccrual,
  } = values;

  const {
    recalcTotalsInTargetAccruals,
    validateValueTarget,
    preValidateValueTarget,
  } = useStudies();

  const [rendFlag, setRendFlag] = useState(false);

  // USED ONLY FOR EnhancedTableHead
  const headCellLabels = {
    value: [`Target Accrual = ${protocolTargetAccrual || "0"}`],
  };

  const sanitizeTargetInput = (event, setFieldValue, fieldName) => {
    const val = event.target.value + "";
    const cleaned = preValidateValueTarget(val);

    if (val !== cleaned) {
      // TODO
      // setFieldTouched(fieldName, true);
      return false;
    } else {
      return true;
    }
  };

  const displayHeadCell = (headCell, t, headCellLabels) =>
    headCellLabels && headCellLabels[headCell.id] ? (
      headCellLabels[headCell.id].length > 1 ? (
        <div style={studyTargetAccrualsStyles.twoRowHeadCell}>
          <span>{headCellLabels[headCell.id][0]}</span>
          <span>{headCellLabels[headCell.id][1]}</span>
        </div>
      ) : (
        headCellLabels[headCell.id][0]
      )
    ) : headCell.label.length > 1 ? (
      <div style={studyTargetAccrualsStyles.twoRowHeadCell}>
        <span>{t("targetAccrualsTable." + headCell.label[0])}</span>
        <span>{t("targetAccrualsTable." + headCell.label[1])}</span>
      </div>
    ) : (
      t("targetAccrualsTable." + headCell.label[0])
    );

  const displayHeadCellWithToolTip = (headCell, t, headCellLabels) => (
    <MuiTooltip
      sx={studyTargetAccrualsStyles.lightTooltip}
      title={t("targetAccrualsTable." + headCell.tooltip)}
      placement="top"
    >
      <div>{displayHeadCell(headCell, t, headCellLabels)}</div>
    </MuiTooltip>
  );

  const EnhancedTableHead = ({ headCellLabels }) => {
    return (
      <MuiTableHead sx={studyTargetAccrualsStyles.studyTableHead}>
        <MuiTableRow>
          {headCells.map((headCell) => (
            <MuiTableCell
              sx={{
                maxWidth: headCell?.maxWidth || "auto",
                minWidth: headCell?.minWidth || "auto",
                width: headCell?.width || "auto",
                color: "white",
                "&:hover": {
                  color: "white",
                },
                "&:focus": {
                  color: "white",
                },
                "&.active": {
                  color: "white",
                },
                padding: "8px",
              }}
              key={headCell.id}
              align={headCell.align || "inherit"}
            >
              {headCell.tooltip
                ? displayHeadCellWithToolTip(headCell, t, headCellLabels)
                : displayHeadCell(headCell, t, headCellLabels)}
            </MuiTableCell>
          ))}
        </MuiTableRow>
      </MuiTableHead>
    );
  };

  //use "-" instaed of "0" when target accrual is zero
  const getProtocolAccrualDependentValues = (value, format) => {
    return protocolTargetAccrual ? applyFormat(value, format) : "-";
  };

  return (
    <MuiPaper sx={studyTargetAccrualsStyles.targetAccrualsRoot}>
      <MuiTableContainer>
        <MuiTable>
          <EnhancedTableHead headCellLabels={headCellLabels} />
          <MuiTableBody>
            {targetAccruals &&
            targetAccruals.categories &&
            targetAccruals.categories.length
              ? targetAccruals.categories.map((categoryObj, index) => {
                  const realTimeCalcForTargetAcc = (categoryObject) => {
                    categoryObject.totals.target = categoryObject.values.reduce(
                      (sum, item) => sum + item.target,
                      0,
                    );

                    const targetFieldValueArray =
                      values.targetAccruals?.categories[index]?.values?.map(
                        (val) => {
                          const sum = roundTo(+val["target"], 4).toFixed(5);
                          return sum * 1;
                        },
                      );

                    recalcTotalsInTargetAccruals(
                      targetAccruals,
                      index,
                      targetFieldValueArray,
                      protocolTargetAccrualc,
                    );

                    return categoryObject.totals.target !== 1 ? true : false;
                  };
                  return (
                    <>
                      <MuiTableRow
                        sx={{
                          backgroundColor: "#F0F4F5",
                        }}
                      >
                        <MuiTableCell
                          sx={{
                            ...studyTargetAccrualsStyles.tableRowCell,
                            color: "inherit",
                            padding: "8px",
                          }}
                          colSpan={headCells.length}
                        >
                          {headCells.map((_, i) =>
                            i === 0 ? categoryObj.label.displayName : "",
                          )}
                        </MuiTableCell>
                      </MuiTableRow>

                      {/*  MAIN IMPLEMENTATINS */}
                      {categoryObj.values.map((valueItem, vInd) => {
                        const fieldNames = categoryObj.values.map(
                          (v, valueIndex) =>
                            `targetAccruals.categories.${index}.values.${valueIndex}.target`,
                        );

                        return (
                          <MuiTableRow key={`${categoryObj.label.id}_${index}`}>
                            {headCells.map((headCellObj) => (
                              <MuiTableCell
                                key={headCellObj.id}
                                align={headCellObj.align || "inherit"}
                                sx={{
                                  ...studyTargetAccrualsStyles.tableRowCell,
                                  padding: "8px",
                                  color:
                                    headCellObj.id === "delta" &&
                                    +valueItem["delta"] < 0
                                      ? "#ff9800"
                                      : "",
                                }}
                              >
                                {headCellObj.id === "target" ? (
                                  <NumericFormat
                                    name={fieldNames[vInd]}
                                    decimalScale={2}
                                    fixedDecimalScale
                                    allowNegative={false}
                                    thousandSeparator
                                    required
                                    isAllowed={({ floatValue }) =>
                                      floatValue === undefined ||
                                      floatValue < 1000
                                    }
                                    disabled={
                                      protocolSummaryAccrual ||
                                      !isOptimizationEnabled
                                    }
                                    suffix="   %"
                                    value={(
                                      targetAccruals.categories[index].values[
                                        vInd
                                      ]["target"] * 100.0 || 0
                                    ).toFixed(2)}
                                    onValueChange={({ floatValue }) => {
                                      const safeValue = floatValue ?? 0;
                                      if (
                                        !sanitizeTargetInput(
                                          { target: { value: safeValue } },
                                          setFieldValue,
                                          fieldNames[vInd],
                                        ) ||
                                        !validateValueTarget(safeValue)
                                      ) {
                                        setRendFlag(!rendFlag);
                                        return;
                                      }

                                      setFieldTouched(fieldNames[vInd], true);
                                      setFieldValue(
                                        fieldNames[vInd],
                                        safeValue / 100,
                                      );
                                      setRendFlag(!rendFlag);
                                    }}
                                    style={{
                                      border: realTimeCalcForTargetAcc(
                                        categoryObj,
                                      )
                                        ? `1px solid ${Theme.palette.pepRed.main}`
                                        : `1px solid ${Theme.palette.grey[400]}`,
                                      color:
                                        protocolSummaryAccrual ||
                                        !isOptimizationEnabled
                                          ? Theme.palette.text.disabled
                                          : Theme.palette.text.primary,

                                      ...studyTargetAccrualsStyles.studyTextField,
                                    }}
                                    error={realTimeCalcForTargetAcc(
                                      categoryObj,
                                    )}
                                  />
                                ) : (
                                  <span>
                                    {[("delta", "actual", "ranking")].includes(
                                      headCellObj.id,
                                    )
                                      ? getProtocolAccrualDependentValues(
                                          valueItem[headCellObj.id],
                                          headCellObj.format,
                                        )
                                      : applyFormat(
                                          valueItem[headCellObj.id],
                                          headCellObj.format,
                                        )}
                                  </span>
                                )}
                              </MuiTableCell>
                            ))}
                          </MuiTableRow>
                        );
                      })}

                      {/*  TOTAL ROW */}
                      <MuiTableRow>
                        {headCells.map((headCellItem) => (
                          <MuiTableCell
                            sx={{
                              ...studyTargetAccrualsStyles.tableRowCell,
                              fontWeight: "bold",
                              padding: "8px",
                            }}
                            key={headCellItem.id}
                            align={headCellItem.align || "inherit"}
                          >
                            {headCellItem.id === "target" ? (
                              <MuiTextField
                                sx={studyTargetAccrualsStyles.totalField}
                                size="small"
                                value={
                                  typeof headCellItem.totalRender === "string"
                                    ? headCellItem.totalRender
                                    : headCellItem.totalRender(
                                        categoryObj.totals[headCellItem.id],
                                      )
                                }
                                error={categoryObj.totals["target"] !== 1}
                                helperText={
                                  categoryObj.totals["target"] !== 1
                                    ? t(`formValidation.wrongTotalPct`)
                                    : ""
                                }
                                name={`categories.${index}.totals.target`}
                                slotProps={{
                                  input: {
                                    endAdornment: (
                                      <MuiInputAdornment position="end">
                                        %
                                      </MuiInputAdornment>
                                    ),
                                  },
                                }}
                                variant="outlined"
                                disabled
                              />
                            ) : typeof headCellItem.totalRender === "string" ? (
                              headCellItem.totalRender
                            ) : (
                              headCellItem.totalRender(
                                categoryObj.totals[headCellItem.id],
                                protocolTargetAccrual,
                              )
                            )}
                          </MuiTableCell>
                        ))}
                      </MuiTableRow>
                    </>
                  );
                })
              : null}
          </MuiTableBody>
        </MuiTable>
      </MuiTableContainer>
    </MuiPaper>
  );
};

export const StudyTargetAccrualsTab = (props) => {
  return <StudyTargetAccrualsTable {...props} />;
};

export default StudyTargetAccrualsTab;
